import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import FeedbackModal from '@/components/feedback/FeedbackModal';
import {
  captureScreenshot,
  getConsoleSnapshot,
  getNetworkSnapshot,
  getDeviceInfo,
} from '@/utils/feedbackCapture';
import styles from './AdoptionSurveys.module.scss';

interface OldSiteExitDialogProps {
  open: boolean;
  onProceed: () => void;
}

export default function OldSiteExitDialog({ open, onProceed }: OldSiteExitDialogProps) {
  const { t } = useTranslation();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [screenshotBase64, setScreenshotBase64] = useState<string | null>(null);
  const [consoleErrors, setConsoleErrors] = useState<object[]>([]);
  const [networkRequests, setNetworkRequests] = useState<object[]>([]);
  const [deviceInfo, setDeviceInfo] = useState<object | null>(null);

  const openFeedback = useCallback(async () => {
    try {
      const screenshot = await captureScreenshot();
      setScreenshotBase64(screenshot);
      setConsoleErrors(getConsoleSnapshot());
      setNetworkRequests(getNetworkSnapshot());
      setDeviceInfo(getDeviceInfo());
    } catch {
      // capture failure shouldn't block the feedback form
    }
    setFeedbackOpen(true);
  }, []);

  const handleSkip = () => {
    onProceed();
  };

  const handleFeedbackClose = () => {
    setFeedbackOpen(false);
    onProceed();
  };

  const handleFeedbackSuccess = () => {
    setFeedbackOpen(false);
    onProceed();
  };

  if (!open) return null;

  if (feedbackOpen) {
    return (
      <FeedbackModal
        isOpen
        onClose={handleFeedbackClose}
        onSubmitSuccess={handleFeedbackSuccess}
        screenshotBase64={screenshotBase64}
        consoleErrors={consoleErrors}
        networkRequests={networkRequests}
        deviceInfo={deviceInfo}
        contextPrefix={t('oldSiteExit.feedbackPrefix')}
      />
    );
  }

  return (
    <div className={styles.modalRoot} role="dialog" aria-modal="true" aria-labelledby="old-site-exit-title">
      <button type="button" className={styles.backdrop} aria-label={t('oldSiteExit.skip')} title={t('oldSiteExit.skip')} onClick={handleSkip} />
      <div className={styles.modal}>
        <h2 id="old-site-exit-title" className="heading-h2-bold">
          {t('oldSiteExit.title')}
        </h2>
        <p className="body-lr-regular text-(--font-sub-title)">{t('oldSiteExit.subtitle')}</p>
        <p className="body-md-regular text-(--font-sub-title)">{t('oldSiteExit.encouragement')}</p>
        <div className={styles.actions} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <button
            type="button"
            className={styles.exitFeedbackCta}
            style={{ backgroundColor: '#73123c', color: '#ffffff', border: 'none' }}
            onClick={openFeedback}
          >
            <FeedbackOutlinedIcon style={{ fontSize: 22 }} />
            <span>{t('oldSiteExit.fillFeedback')}</span>
          </button>
          <button
            type="button"
            className={styles.skipLink}
            style={{ background: 'transparent', border: 'none', color: '#888', textDecoration: 'underline' }}
            onClick={handleSkip}
          >
            {t('oldSiteExit.skip')} &larr;
          </button>
        </div>
      </div>
    </div>
  );
}
